import React, { useEffect, useRef, useState } from 'react';
import authApi, { ChatMessageResponse } from '../../services/authApi';
import { useAuth } from '../../context/AuthContext';
import { useChatBadge } from '../../context/ChatBadgeContext';
import PageGate from '../PageGate';
import './tool-page.css';
import './ChatSupportPage.css';

const POLL_INTERVAL = 30000;

const ChatSupportPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { setUnread } = useChatBadge();

  const [messages, setMessages]         = useState<ChatMessageResponse[]>([]);
  const [adminLastActive, setAdminLastActive] = useState<string | null>(null);
  const [input, setInput]               = useState('');
  const [sending, setSending]           = useState(false);
  const [loading, setLoading]           = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pollRef   = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    load();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isAuthenticated]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const load = async () => {
    try {
      const [msgRes, countRes] = await Promise.all([
        authApi.getChatMessages(),
        authApi.getChatUnreadCount(),
      ]);
      setMessages(msgRes.data ?? []);
      setAdminLastActive(countRes.data?.adminLastActiveAt ?? null);
      setUnread(0); // clear sidebar badge when user opens chat
    } catch {}
    finally { setLoading(false); }
  };

  const poll = async () => {
    try {
      const countRes = await authApi.getChatUnreadCount();
      setAdminLastActive(countRes.data?.adminLastActiveAt ?? null);
      if ((countRes.data?.unreadCount ?? 0) > 0) {
        const res = await authApi.getChatMessages();
        setMessages(res.data ?? []);
      }
    } catch {}
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await authApi.sendChatMessage(input.trim());
      setMessages((prev) => [...prev, res.data]);
      setInput('');
    } catch {}
    finally { setSending(false); }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const adminStatus = getAdminStatus(adminLastActive);

  return (
    <PageGate>
    <div className="tool-page csp-page">
      <div className="csp-shell">

        {/* Panel header */}
        <div className="csp-panel-header">
          <div className="csp-avatar-wrap">
            <div className="csp-panel-avatar">S</div>
            <span className={`csp-dot ${adminStatus.online ? 'csp-dot--online' : 'csp-dot--offline'}`} />
          </div>
          <div>
            <div className="csp-panel-name">Support Team</div>
            <div className="csp-panel-status">
              {adminStatus.online
                ? <span className="csp-status-online">● Online</span>
                : <span className="csp-status-offline">{adminStatus.detail}</span>
              }
            </div>
          </div>
        </div>

        <div className="csp-messages">
          {loading ? (
            <div className="csp-loading">Loading messages…</div>
          ) : messages.length === 0 ? (
            <div className="csp-welcome">
              <div className="csp-welcome-icon">💬</div>
              <div className="csp-welcome-title">Hi {user?.name?.split(' ')[0] || 'there'}!</div>
              <div className="csp-welcome-sub">Ask us anything about your subscription, learning, or account.</div>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`csp-msg ${m.senderType === 'USER' ? 'csp-msg--user' : 'csp-msg--admin'}`}>
                {m.senderType === 'ADMIN' && (
                  <div className="csp-msg-avatar">S</div>
                )}
                <div className="csp-msg-body">
                  <div className="csp-bubble">{m.content}</div>
                  <div className="csp-time">{formatTime(m.createdAt)}</div>
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        <div className="csp-input-bar">
          <textarea
            className="csp-input"
            placeholder="Type your message… (Enter to send)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            rows={1}
          />
          <button className="csp-send" onClick={send} disabled={sending || !input.trim()}>
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
    </PageGate>
  );
};

function getAdminStatus(lastActiveAt: string | null): { online: boolean; detail: string } {
  if (!lastActiveAt) return { online: false, detail: 'Usually replies within a few hours' };
  const last = new Date(lastActiveAt);
  const diffMin = Math.floor((Date.now() - last.getTime()) / 60000);
  if (diffMin < 2)  return { online: true,  detail: 'Online' };
  if (diffMin < 60) return { online: false, detail: `Last active ${diffMin} min ago` };
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24)  return { online: false, detail: `Last active ${diffHr}h ago` };
  return { online: false, detail: 'Usually replies within a few hours' };
}

function formatTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' }) +
      ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

export default ChatSupportPage;
